import styled, { css, keyframes } from 'styled-components';

import {
  HERO_FONT_BODY,
  HERO_FONT_DISPLAY,
  LandingHero,
} from 'constants/landing-hero';

const MOBILE = '@media (max-width: 760px)';

const accentOf = (accent: LandingV2.PersonaAccent) =>
  accent === 'pro' ? LandingHero.pro : LandingHero.creator;

const ping = keyframes`
  0% { transform: scale(1); opacity: 0.55; }
  100% { transform: scale(1.9); opacity: 0; }
`;

export const Card = styled.article<{ $accent: LandingV2.PersonaAccent }>`
  position: relative;
  padding: clamp(2.8rem, 3.5vh, 4rem);
  border-radius: 3.2rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  background: ${({ $accent }) => accentOf($accent).cardBg};
  border: 0.1rem solid ${({ $accent }) => accentOf($accent).cardBorder};
  box-shadow:
    0 3rem 6rem -3rem ${LandingHero.cardShadow},
    inset 0 0.1rem 0 ${LandingHero.cardInsetHighlight};
  backdrop-filter: blur(1.6rem);
  -webkit-backdrop-filter: blur(1.6rem);
  transition:
    transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1),
    box-shadow 0.45s ease;

  &::before {
    content: '';
    position: absolute;
    inset: -0.1rem;
    border-radius: inherit;
    padding: 0.1rem;
    background: ${({ $accent }) => accentOf($accent).ring};
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0.35;
    transition: opacity 0.45s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-0.8rem);
    box-shadow:
      0 4rem 8rem -3rem ${({ $accent }) => accentOf($accent).cardHoverShadow},
      0 0 0 0.1rem ${({ $accent }) => accentOf($accent).cardHoverRing},
      inset 0 0.1rem 0 ${LandingHero.cardInsetHighlightHover};
  }

  &:hover::before {
    opacity: 1;
  }

  ${MOBILE} {
    padding: 2.8rem;
  }
`;

export const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 2rem;
`;

export const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const Kicker = styled.span<{ $accent: LandingV2.PersonaAccent }>`
  font-family: ${HERO_FONT_BODY};
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: 0.2rem;
  text-transform: uppercase;
  color: ${({ $accent }) => accentOf($accent).kicker};
`;

export const Title = styled.h2`
  margin: 0;
  font-family: ${HERO_FONT_DISPLAY};
  font-weight: 800;
  font-size: 3.4rem;
  letter-spacing: -0.1rem;
  color: ${LandingHero.textPrimary};
`;

export const Arrow = styled.span<{ $accent: LandingV2.PersonaAccent }>`
  position: relative;
  isolation: isolate;
  flex: none;
  display: grid;
  place-items: center;
  width: 6rem;
  height: 6rem;
  border-radius: 2rem;
  background: ${({ $accent }) => accentOf($accent).arrowGradient};
  color: ${({ $accent }) => accentOf($accent).arrowText};
  box-shadow:
    0 1.2rem 3rem -0.8rem ${({ $accent }) => accentOf($accent).arrowShadow},
    inset 0 0.1rem 0 ${LandingHero.ctaInsetHighlight};
  transition: transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: inherit;
    animation: ${ping} 2.4s ease-out infinite;
    z-index: -1;
  }

  svg {
    transition: transform 0.35s ease;
  }

  ${Card}:hover & {
    transform: translateX(0.4rem) rotate(-8deg);
  }

  ${Card}:hover & svg {
    transform: translateX(0.3rem);
  }
`;

export const Description = styled.p`
  margin: clamp(1.4rem, 2vh, 2rem) 0 0;
  font-family: ${HERO_FONT_BODY};
  font-size: 1.6rem;
  line-height: 1.65;
  color: ${LandingHero.textMutedSoft};
`;

export const Divider = styled.div<{ $accent: LandingV2.PersonaAccent }>`
  height: 0.1rem;
  margin: clamp(1.8rem, 2.5vh, 2.8rem) 0;
  background: ${({ $accent }) => accentOf($accent).divider};
`;

export const ChipRow = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const chipHover = (accent: LandingV2.PersonaAccent) => {
  const tones = accentOf(accent);
  return css`
    color: ${tones.chipHoverText};
    background: ${tones.chipHoverBg};
    border-color: ${tones.chipHoverBorder};
    box-shadow: 0 0.8rem 2rem -1rem ${tones.chipHoverShadow};
  `;
};

export const Chip = styled.li<{ $accent: LandingV2.PersonaAccent }>`
  padding: 1rem 1.6rem;
  border-radius: 1.2rem;
  background: ${LandingHero.chipBg};
  border: 0.1rem solid ${LandingHero.chipBorder};
  font-family: ${HERO_FONT_BODY};
  font-size: 1.4rem;
  font-weight: 700;
  color: ${LandingHero.chipText};
  transition:
    transform 0.25s ease,
    background-color 0.25s ease,
    border-color 0.25s ease,
    color 0.25s ease,
    box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-0.2rem);
    ${({ $accent }) => chipHover($accent)}
  }
`;
